import { ArrowRight, Sparkles } from "lucide-react";

/**
 * Starter prompts. Deliberately concrete Salesforce scenarios rather than feature descriptions — a
 * first-time user should be able to click one and watch the whole loop happen.
 */
const STARTER_PROMPTS = [
	"Create an Account and verify it appears in the list view",
	"Check that a required field blocks saving a new Contact",
	"Convert a Lead and verify the Opportunity is created",
] as const;

/**
 * The Testing module's ONE empty state.
 *
 * There used to be three, one per column: the sidebar's "No tests yet", this chat CTA, and a third in
 * the right panel that additionally carried the Suites and Coverage buttons. All three competed for
 * the same first decision. The side panels now stay quiet when there is nothing to show, and this is
 * the only thing that asks the user for something.
 *
 * `ChatEmptyHint`, the former second variant, was unreachable: it only rendered when
 * `messages.length === 0 && chat.streaming`, and `send()` appends the user + assistant messages
 * before streaming begins, so that combination never occurred.
 */
export function ChatEmptyCta({ onSuggestion }: { onSuggestion: (text: string) => void }) {
	return (
		<div className="tst-measure flex flex-1 flex-col items-center justify-center gap-5 py-10 text-center">
			<span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
				<Sparkles className="h-5 w-5" />
			</span>
			<div className="max-w-md space-y-1.5">
				<h2 className="tst-hero">Describe a test in plain English</h2>
				<p className="text-[13px] leading-relaxed text-muted-foreground">
					The agent inspects your org's objects, fields and record types, writes the test, runs it in
					a real browser, and proposes a fix for your review when it fails.
				</p>
			</div>
			<div className="flex w-full max-w-md flex-col gap-1.5">
				{STARTER_PROMPTS.map((prompt) => (
					<button
						key={prompt}
						type="button"
						onClick={() => onSuggestion(prompt)}
						className="tst-interactive group flex items-center gap-2 rounded-lg border border-border/60 bg-card/40 px-3 py-2.5 text-left text-[13px] text-foreground/85 hover:border-primary/40 hover:bg-primary/5 hover:text-foreground"
					>
						<span className="min-w-0 flex-1">{prompt}</span>
						<ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50 transition-colors group-hover:text-primary" />
					</button>
				))}
			</div>
			<p className="text-[11px] text-muted-foreground/70">
				Or press ⌘⇧N for a new test at any time.
			</p>
		</div>
	);
}
